import { GridApi, RowDragEndEvent, RowDragMoveEvent } from 'ag-grid-community';
import { RowDataTransaction } from 'ag-grid-enterprise';
import { has, mapKeys } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridDataRow, AgGridRowsReorderResponse, AgGridTransactionOptions } from 'core/types';
import { isEmptyObject } from 'core/utils';

let initialReorderRowData: AgGridDataRow[] = [];
let diffReorderRowData: Record<string, AgGridDataRow> = {};

interface IProps {
  gridApi: GridApi;
  rows: AgGridDataRow[];
  onRowsReorder: (diffRows: AgGridDataRow[]) => AgGridRowsReorderResponse;
  handleUpdateRows: (
    rowDataTransaction: RowDataTransaction<AgGridDataRow>,
    options?: AgGridTransactionOptions
  ) => void;
  handleReplaceRows: (
    oldRows: AgGridDataRow[],
    newRows: AgGridDataRow[],
    options?: AgGridTransactionOptions
  ) => void;
}

export const useAgGridRowDragging = ({
  gridApi,
  rows,
  onRowsReorder,
  handleUpdateRows,
  handleReplaceRows,
}: IProps) => {
  const { t } = useTranslation();

  const setInitialReorderRowData = useCallback(
    (rows: AgGridDataRow[]) => (initialReorderRowData = [...rows]),
    []
  );

  const onRowDragEnter = () => setInitialReorderRowData(rows);

  const onRowDragMove = useCallback(
    ({ node: movingNode, overNode }: RowDragMoveEvent) => {
      if (movingNode?.id === overNode?.id) {
        return;
      }

      // Reordering not allowed for group rows
      if (overNode?.group) {
        return;
      }

      // Reordering not allowed on non displayed rows
      if (!overNode?.displayed) {
        return;
      }

      if (overNode?.data?.deleted) {
        return;
      }

      const movingData = movingNode.data;
      const overData = overNode.data;

      // If the over row is an alternate part, use the parent id
      const overDataId = overData.masterQuotePartId > 0 ? overData.masterQuotePartId : overData.id;

      let overDataLine = movingData.line;

      // If over data is a substituted row, already have been added to the diffReorderRowData
      // (through its alternates) and the moving data has already the same line as the substituted
      // one, the line should be increased with 1
      if (
        overData.substituted &&
        has(diffReorderRowData, overData.id) &&
        movingData.line === overData.line
      ) {
        overDataLine = overDataLine + 1;
      }

      // If over data is an alternate row, already have been added to the diffReorderRowData
      // (through its parent) and the moving data has already the same line as the alternate
      // one, the line should be decreased with 1
      if (
        overData.masterQuotePartId > 0 &&
        has(diffReorderRowData, overData.masterQuotePartId) &&
        movingData.line === overData.line
      ) {
        overDataLine = overDataLine - 1;
      }

      const diffTableData = {
        [movingData.id]: {
          ...movingData,
          line: overData.line,
        },
        [overDataId]: {
          ...overData,
          line: overDataLine,
        },
      };

      handleUpdateRows({
        update: [...Object.values(diffTableData)],
      });

      diffReorderRowData = { ...diffReorderRowData, ...diffTableData };

      gridApi.clearFocusedCell();
    },
    [gridApi, handleUpdateRows]
  );

  const onRowDragEnd = useCallback(
    async ({ node: { data: movingData }, overIndex }: RowDragEndEvent) => {
      const overData = initialReorderRowData[overIndex];

      const overDataMasterId = overData?.masterQuotePartId || overData?.id;

      if (overDataMasterId === movingData.id) {
        return;
      }

      try {
        if (isEmptyObject(diffReorderRowData)) {
          throw new Error(t('errorInvalidReordering'));
        }

        gridApi.setGridOption('suppressRowDrag', true);

        const { success, updatedRows } = await onRowsReorder(Object.values(diffReorderRowData));

        if (!success) {
          throw new Error(t('errorReorderingFailed'));
        }

        const updatedRowMapped = mapKeys(updatedRows, (row) => row.id);
        const removeRows = rows?.filter((row) => has(updatedRowMapped, row.id));

        handleReplaceRows(removeRows, updatedRows);
      } catch (error) {
        handleReplaceRows(rows, initialReorderRowData);
      } finally {
        setInitialReorderRowData([]);
        diffReorderRowData = {};

        gridApi.clearFocusedCell();

        gridApi.setGridOption('suppressRowDrag', false);
      }
    },
    [gridApi, handleReplaceRows, onRowsReorder, rows, setInitialReorderRowData, t]
  );

  return {
    setInitialReorderRowData,
    onRowDragEnter,
    onRowDragMove,
    onRowDragEnd,
  };
};
